import React, { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip as LeafletTooltip } from 'react-leaflet';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as RechartTooltip, BarChart, Bar } from "recharts";
import { PieChart, Pie, Cell } from "recharts";
import {
  LuActivity, LuGlobe, LuSmartphone, LuMonitor,
  LuTablet, LuList, LuChevronLeft, LuChevronRight,
  LuArrowUpDown, LuRefreshCw, LuToggleLeft, LuToggleRight,
  LuShare2, LuUserCheck, LuTrendingUp, LuFilter
} from "react-icons/lu";
import 'leaflet/dist/leaflet.css';
import "../styles/AdminAnalysis.css";

const COUNTRY_COORDS_LEAFLET = {
  KR: [35.9, 127.7], CN: [35.8, 104.1], JP: [36.2, 138.2],
  US: [37.0, -95.7], TW: [23.5, 121.0], SG: [1.3, 103.8],
  HK: [22.3, 114.1], VN: [14.0, 108.2], TH: [15.8, 100.9],
  PH: [12.8, 121.7], ID: [-0.7, 113.9], MY: [4.2, 101.9],
  BR: [-14.2, -51.9], FR: [46.2, 2.2], DE: [51.1, 10.4],
  GB: [55.3, -3.4], CA: [56.1, -106.3], AU: [-25.2, 133.7],
};

export default function AdminAnalysis() {
  const [viewMode, setViewMode] = useState("REALTIME");
  const [period, setPeriod] = useState("7D");
  const [chartMode, setChartMode] = useState("users");
  const [data, setData] = useState({ active_users: 0, countries: [], devices: [], sessions: [] });
  const [history, setHistory] = useState({ log_data: [] });

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date().toLocaleTimeString());

  const [selectedIp, setSelectedIp] = useState(null);
  const [traceData, setTraceData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "visited_at", direction: "desc" });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [filterCountry, setFilterCountry] = useState("ALL");
  const [filterEnv, setFilterEnv] = useState("ALL");

  const COLORS = ["#c4a77d", "#8c7a5d", "#5e523e", "#3a3326", "#a08a6d", "#d8c8a8"];

  const getEnvironmentLabel = (userAgent = "") => {
    const ua = (userAgent || "").toLowerCase();
    if (ua.includes("googlebot") || ua.includes("googleother")) return "Google Bot";
    if (ua.includes("applebot")) return "Apple Bot";
    if (ua.includes("bingbot")) return "Bing Bot";
    if (ua.includes("yeti")) return "Naver Bot";

    if (ua.includes("kakaotalk")) return "KakaoTalk";
    if (ua.includes("naver(inapp")) return "Naver App";
    if (ua.includes("itmedia") || ua.includes("nlab")) return "Netolab App";
    if (ua.includes("twitter") || ua.includes("twttr")) return "X (Twitter App)";
    if (ua.includes("instagram")) return "Instagram App";
    if (ua.includes("fban") || ua.includes("fbav")) return "Facebook App";
    if (ua.includes("threads")) return "Threads App";
    if (ua.includes("line/")) return "Line";
    if (ua.includes("youtube")) return "YouTube App";
    if (ua.includes("tiktok")) return "TikTok";
    if (ua.includes("yahooapp") || ua.includes("yjapp")) return "Yahoo! App";
    if (ua.includes("ameba")) return "Ameba App";
    if (ua.includes("weibo")) return "Weibo App";
    if (ua.includes("micromessenger")) return "WeChat App";
    if (ua.includes("kanzhun")) return "Xiaohongshu App";

    let devicePrefix = "PC";
    if (ua.includes("mobile") || ua.includes("android") || ua.includes("iphone") || ua.includes("ipad")) {
      devicePrefix = "Mobile";
    }
    if (ua.includes("edg/")) return `Edge (${devicePrefix})`;
    if (ua.includes("whale/")) return `Whale (${devicePrefix})`;
    if (ua.includes("samsungbrowser")) return `Samsung (${devicePrefix})`;
    if (ua.includes("chrome")) return `Chrome (${devicePrefix})`;
    if (ua.includes("firefox")) return `Firefox (${devicePrefix})`;
    if (ua.includes("safari") || ua.includes("applewebkit")) return `Safari (${devicePrefix})`;
    return "Unknown Client";
  };

  const getSourceLabel = (url, userAgent = "") => {
    const ua = (userAgent || "").toLowerCase();
    if (!url || url === "Direct") {
      if (ua.includes("instagram")) return "Instagram";
      if (ua.includes("twitter") || ua.includes("twttr")) return "X (Twitter)";
      if (ua.includes("yahoo")) return "Yahoo";
      if (ua.includes("ameba")) return "Ameba";
      if (ua.includes("weibo")) return "Weibo";
      if (ua.includes("micromessenger")) return "WeChat";
      if (ua.includes("kakaotalk")) return "KakaoTalk";
      if (ua.includes("threads")) return "Threads";
      if (ua.includes("line/")) return "Line";
      if (ua.includes("youtube")) return "YouTube";
      if (ua.includes("tiktok")) return "TikTok";
      if (ua.includes("fban") || ua.includes("fbav")) return "Facebook";
      if (ua.includes("itmedia") || ua.includes("nlab")) return "Netolab App";
      return "Direct";
    }
    try {
      const urlObj = new URL(url);
      const params = urlObj.searchParams;
      const utmSource = params.get("utm_source");
      const utmContent = params.get("utm_content");
      if (utmSource) {
        let label = "";
        if (utmSource === "ig") label = "Instagram";
        else if (utmSource === "tw" || utmSource === "x") label = "X (Twitter)";
        else label = utmSource.toUpperCase();
        if (utmContent === "link_in_bio") label += " (Bio)";
        return label;
      }

      if (params.has("fbclid") || params.has("igsh")) return "Social Post/Share";

      const hostname = urlObj.hostname.toLowerCase();
      if (hostname === "hanjiwoo.com" || hostname === "www.hanjiwoo.com") return "Direct";
      if (hostname.includes("nlab.itmedia.co.jp")) return "Netolab (Japan)"; 
      if (hostname.includes("itmedia.co.jp")) return "ITmedia News";
      if (hostname.includes("yahoo.co.jp")) return "Yahoo Japan";
      if (hostname.includes("ameblo.jp") || hostname.includes("ameba.jp")) return "Ameba Blog";
      if (hostname.includes("5ch.net") || hostname.includes("2ch.net")) return "5ch Community";
      if (hostname.includes("note.com")) return "Note.com";
      if (hostname.includes("baidu.com")) return "Baidu";
      if (hostname.includes("weibo.com")) return "Weibo";
      if (hostname.includes("xiaohongshu.com")) return "Xiaohongshu";
      if (hostname.includes("reddit.com")) return "Reddit";
      if (hostname.includes("linktr.ee")) return "Linktree";
      if (hostname.includes("yahoo.com")) return "Yahoo";
      if (hostname.includes("bing.com")) return "Bing"
      if (hostname.includes("dcard.tw")) return "Dcard (Taiwan)";

      if (hostname.includes("ptt.cc")) return "PTT Community";
      if (hostname.includes("t.co")) return "X (Twitter)";
      if (hostname.includes("ameblo.jp") || hostname.includes("ameba.jp")) return "Ameba Blog";
      if (hostname.includes("naver.com")) return "Naver";
      if (hostname.includes("google")) return "Google";
      if (hostname.includes("instagram.com")) return "Instagram";
      if (hostname.includes("facebook.com") || hostname.includes("m.facebook.com")) return "Facebook";
      if (hostname.includes("threads.net")) return "Threads";
      return hostname.replace("www.", "");
    } catch (e) { return "Direct"; }
  };

  // 📍 1. 모든 차트와 표의 근간이 되는 필터링된 로그 데이터셋 생성
  const filteredLogs = useMemo(() => {
    const baseItems = viewMode === "REALTIME" ? data.sessions : history.log_data;
    return baseItems.filter(item => {
      const matchCountry = filterCountry === "ALL" || item.country_code === filterCountry;
      const matchEnv = filterEnv === "ALL" || getEnvironmentLabel(item.user_agent) === filterEnv;
      return matchCountry && matchEnv;
    });
  }, [data.sessions, history.log_data, viewMode, filterCountry, filterEnv]);

  // 📍 2. 필터링된 데이터를 바탕으로 스택형 차트 데이터 생성
  const dynamicHistoryData = useMemo(() => {
    if (filteredLogs.length === 0) return { chartData: [], keys: [] };
    const dailyMap = {};
    const categories = new Set();

    filteredLogs.forEach((log) => {
      const date = (log.visited_at || "").substring(0, 10);
      if (!date) return;
      if (!dailyMap[date]) dailyMap[date] = { date, users: 0, ips: new Set() };
      dailyMap[date].users += 1;
      dailyMap[date].ips.add(log.ip_address);

      const env = getEnvironmentLabel(log.user_agent);
      let category = (env.includes("(") && env.includes(")")) ? "General Browser" : env;
      if (category.includes("Bot")) return;
      dailyMap[date][category] = (dailyMap[date][category] || 0) + 1;
      categories.add(category);
    });

    const chartData = Object.values(dailyMap).map((day) => {
      const uniqueIps = day.ips.size;
      const retention = day.users > 0 ? Math.round(((day.users - uniqueIps) / day.users) * 100) : 0;
      return { ...day, retention };
    }).sort((a, b) => a.date.localeCompare(b.date));

    const keys = Array.from(categories).sort((a, b) => (a === "General Browser" ? 1 : -1));
    return { chartData, keys };
  }, [filteredLogs]);

  // 📍 3. 필터링된 데이터를 바탕으로 하단 그리드 통계 재계산
  const displayCountries = useMemo(() => {
    const countryMap = {};
    filteredLogs.forEach(log => {
      const code = log.country_code || "Unknown";
      countryMap[code] = (countryMap[code] || 0) + 1;
    });
    return Object.entries(countryMap).map(([code, count]) => ({ code, count })).sort((a, b) => b.count - a.count);
  }, [filteredLogs]);

  const displayDevices = useMemo(() => {
    const envMap = {};
    filteredLogs.forEach(log => {
      const label = getEnvironmentLabel(log.user_agent);
      envMap[label] = (envMap[label] || 0) + 1;
    });
    return Object.entries(envMap).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filteredLogs]);

  const sourceStats = useMemo(() => {
    const statsMap = {};
    filteredLogs.forEach(log => {
      const label = getSourceLabel(log.referer, log.user_agent);
      statsMap[label] = (statsMap[label] || 0) + 1;
    });
    return Object.entries(statsMap).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filteredLogs]);

  const totalInsights = useMemo(() => {
    if (filteredLogs.length === 0) return null;
    const uniqueIps = new Set(filteredLogs.map((l) => l.ip_address)).size;
    const overallRetention = Math.round(((filteredLogs.length - uniqueIps) / filteredLogs.length) * 100);
    const viralMap = {};
    filteredLogs.forEach((log) => {
      const env = getEnvironmentLabel(log.user_agent);
      if (!env.includes("(")) viralMap[env] = (viralMap[env] || 0) + 1;
    });
    const topViral = Object.entries(viralMap).sort((a, b) => b[1] - a[1]).map(([name, count]) => ({
      name, count, percent: Math.round((count / filteredLogs.length) * 100)
    }));
    return { overallRetention, topViral, totalViralCount: Object.values(viralMap).reduce((a, b) => a + b, 0) };
  }, [filteredLogs]);

  // 📍 필터 옵션 추출 (원본 데이터 기준)
  const filterOptions = useMemo(() => {
    const baseItems = viewMode === "REALTIME" ? data.sessions : history.log_data;
    const countries = new Set();
    const envs = new Set();
    baseItems.forEach(item => {
      countries.add(item.country_code);
      envs.add(getEnvironmentLabel(item.user_agent));
    });
    return {
      countries: ["ALL", ...Array.from(countries).sort()],
      envs: ["ALL", ...Array.from(envs).sort()]
    };
  }, [data.sessions, history.log_data, viewMode]);

  // 📍 테이블 페이지네이션용 데이터
  const pagedItems = useMemo(() => {
    const sorted = [...filteredLogs];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        const aVal = a[sortConfig.key] || "";
        const bVal = b[sortConfig.key] || "";
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sorted.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [filteredLogs, sortConfig, currentPage]);

  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const maxCountryCount = displayCountries.length > 0 ? Math.max(...displayCountries.map((c) => c.count)) : 1;

  const fetchData = async () => {
    setIsRefreshing(true);
    try {
      const url = viewMode === "REALTIME" ? "admin_monitor.php" : `admin_stats_history.php?period=${period}`;
      const res = await fetch(`https://hanjiwoo.com/api/${url}`, { credentials: "include" });
      const result = await res.json();
      if (res.ok && result.success !== false) {
        if (viewMode === "REALTIME") setData(result);
        else setHistory({ log_data: result.log_data || [] });
      }
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (err) { console.error(err); }
    finally { setTimeout(() => setIsRefreshing(false), 600); }
  };

  const handleIpClick = async (ip) => {
    setSelectedIp(ip);
    try {
      const res = await fetch(`https://hanjiwoo.com/api/admin_user_trace.php?ip=${encodeURIComponent(ip)}`, { credentials: "include" });
      const result = await res.json();
      setTraceData(result.trace_data || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    fetchData();
    let timer;
    if (viewMode === "REALTIME" && autoRefresh) timer = setInterval(fetchData, 10000);
    return () => clearInterval(timer);
  }, [viewMode, period, autoRefresh]);

  useEffect(() => {
    setCurrentPage(1);
    setSortConfig({ key: viewMode === "REALTIME" ? "last_activity" : "visited_at", direction: "desc" });
  }, [viewMode, period]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const getDeviceIcon = (name) => {
    const upper = (name || "").toUpperCase();
    if (upper.includes("MOBILE") || upper.includes("TABLET") || upper.includes("APP")) return <LuSmartphone />;
    return <LuMonitor />;
  };

  const CustomTooltip = ({ active, payload, label, chartMode }) => {
    if (active && payload && payload.length) {
      if (chartMode === "viral") {
        const sorted = [...payload].sort((a, b) => b.value - a.value);
        return (
          <div className="custom-chart-tooltip">
            <p className="tooltip-date">{label}</p>
            <hr className="tooltip-divider" />
            {sorted.map((entry, idx) => (
              <div key={idx} className="detail-item">
                <span className="detail-name" style={{ color: entry.color }}>{entry.name}</span>
                <span className="detail-count">{entry.value}명</span>
              </div>
            ))}
          </div>
        );
      }
      return (
        <div className="custom-chart-tooltip">
          <p className="tooltip-date">{label}</p>
          <hr className="tooltip-divider" />
          <p className="tooltip-main-val">{chartMode === "users" ? "방문자수" : "재방문율"}: <span>{payload[0].value}{chartMode === "retention" ? "%" : "명"}</span></p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="sub-page-container admin-page analysis-page">
      <div className="admin-inner">
        <section className="admin-card main-visual-card">
          <div className="card-head align-between">
            <div className="title-side">
              <h3>{viewMode === "REALTIME" ? <><LuGlobe className="card-icon" /> GLOBAL REACH</> : <><LuActivity className="card-icon" /> VISITOR TRENDS</>}</h3>
              {viewMode === "REALTIME" && <span className="live-indicator pulse">LIVE</span>}
            </div>
            <div className="analysis-controls">
              <div className="update-info">Last updated: <span>{lastUpdated}</span></div>
              <button className={`auto-refresh-toggle ${autoRefresh ? "on" : "off"}`} onClick={() => setAutoRefresh(!autoRefresh)}>
                {autoRefresh ? <LuToggleRight size={28} /> : <LuToggleLeft size={28} />}
                <span>AUTO</span>
              </button>
              <button className={`refresh-btn ${isRefreshing ? "spinning" : ""}`} onClick={fetchData} disabled={isRefreshing}><LuRefreshCw /></button>
              <div className="view-selector">
                <button className={viewMode === "REALTIME" ? "active" : ""} onClick={() => setViewMode("REALTIME")}>REAL-TIME</button>
                <button className={viewMode === "HISTORY" ? "active" : ""} onClick={() => setViewMode("HISTORY")}>HISTORY</button>
              </div>
            </div>
          </div>

          <div className="main-content-wrapper">
            {viewMode === "REALTIME" ? (
              <MapContainer center={[20, 127]} zoom={2} style={{ height: "400px", background: "#050505" }}>
                <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
                {displayCountries.map((c) => COUNTRY_COORDS_LEAFLET[c.code] && (
                  <CircleMarker key={c.code} center={COUNTRY_COORDS_LEAFLET[c.code]} radius={Math.max(8, c.count * 2)} fillColor="#c4a77d" color="#fff" weight={1} fillOpacity={0.7}>
                    <LeafletTooltip direction="top">{c.code}: {c.count}</LeafletTooltip>
                  </CircleMarker>
                ))}
              </MapContainer>
            ) : (
              <div className="history-chart-area">
                <div className="history-chart-header">
                  <div className="chart-mode-tabs">
                    <button className={chartMode === "users" ? "active" : ""} onClick={() => setChartMode("users")}><LuTrendingUp /> 방문자수</button>
                    <button className={chartMode === "retention" ? "active" : ""} onClick={() => setChartMode("retention")}><LuUserCheck /> 재방문율</button>
                    <button className={chartMode === "viral" ? "active" : ""} onClick={() => setChartMode("viral")}><LuShare2 /> 유입환경</button>
                  </div>
                  {totalInsights && (
                    <div className="period-insight-summary">
                      <div className="insight-item"><span className="ins-label">선택 데이터 충성도</span><span className="ins-value">{totalInsights.overallRetention}% <small>Re-visit</small></span></div>
                      <div className="insight-item"><span className="ins-label">선택 데이터 주력 채널</span><span className="ins-value">{totalInsights.topViral[0] ? <>{totalInsights.topViral[0].name} <small>({totalInsights.topViral[0].percent}%)</small></> : "N/A"}</span></div>
                    </div>
                  )}
                  <div className="period-selector">
                    {["7D", "30D", "ALL"].map((p) => <button key={p} className={period === p ? "active" : ""} onClick={() => setPeriod(p)}>{p}</button>)}
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={350}>
                  {chartMode === "viral" ? (
                    <BarChart data={dynamicHistoryData.chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                      <XAxis dataKey="date" stroke="#666" fontSize={12} tickFormatter={(v) => v.split("-").slice(1).join("/")} />
                      <YAxis stroke="#666" fontSize={12} />
                      <RechartTooltip content={<CustomTooltip chartMode="viral" />} />
                      {dynamicHistoryData.keys.map((key, idx) => (
                        <Bar key={key} dataKey={key} stackId="a" fill={key === "General Browser" ? "#3a3326" : COLORS[idx % COLORS.length]} />
                      ))}
                    </BarChart>
                  ) : (
                    <LineChart data={dynamicHistoryData.chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                      <XAxis dataKey="date" stroke="#666" fontSize={12} tickFormatter={(v) => v.split("-").slice(1).join("/")} />
                      <YAxis stroke="#666" fontSize={12} unit={chartMode === "retention" ? "%" : ""} />
                      <RechartTooltip content={<CustomTooltip chartMode={chartMode} />} />
                      <Line type="monotone" dataKey={chartMode} stroke="#c4a77d" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 8 }} />
                    </LineChart>
                  )}
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </section>

        <div className="analysis-bottom-grid four-cols">
          <section className="admin-card summary-card">
            <div className="stat-label">FILTERED VISITORS</div>
            <div className="stat-value-group"><span className="stat-value">{filteredLogs.length.toLocaleString()}</span><LuActivity className="pulse-icon" /></div>
          </section>
          <section className="admin-card country-list-card">
            <div className="card-head"><h3>COUNTRIES</h3></div>
            <div className="country-list-scroll">
              {displayCountries.map((c, i) => (
                <div key={i} className="country-item"><span className="country-name">{c.code}</span>
                  <div className="count-bar-wrapper"><div className="count-bar" style={{ width: `${(c.count / maxCountryCount) * 100}%` }}></div><span className="count-num">{c.count}</span></div>
                </div>
              ))}
            </div>
          </section>
          <section className="admin-card device-card">
            <div className="card-head"><h3>ENVIRONMENTS</h3></div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={120}>
                <PieChart><Pie data={displayDevices} innerRadius={35} outerRadius={50} dataKey="value">{displayDevices.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie></PieChart>
              </ResponsiveContainer>
              <div className="source-list">
                {displayDevices.map((d, i) => (
                  <div key={i} className="source-item">
                    <div className="source-left"><span className="dot" style={{ background: COLORS[i % COLORS.length] }}></span><span className="device-icon" style={{ fontSize: '0.9rem' }}>{getDeviceIcon(d.name)}</span><span className="label-analysis" style={{ fontSize: '0.75rem' }}>{d.name}</span></div>
                    <span className="val-analysis">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <section className="admin-card source-card">
            <div className="card-head"><h3>TOP SOURCES</h3></div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={120}>
                <PieChart><Pie data={sourceStats} innerRadius={35} outerRadius={50} dataKey="value">{sourceStats.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie></PieChart>
              </ResponsiveContainer>
              <div className="source-list">
                {sourceStats.map((s, i) => (
                  <div key={i} className="source-item"><div className="source-left"><span className="dot" style={{ background: COLORS[i % COLORS.length] }}></span><span className="label-analysis">{s.name}</span></div><span className="val-analysis">{s.value}</span></div>
                ))}
              </div>
            </div>
          </section>
        </div>

        <section className="admin-card table-card full-width">
          <div className="card-head align-between">
            <div className="table-title-side">
              <h3><LuList className="card-icon" /> {viewMode === "REALTIME" ? "ACTIVE SESSIONS" : "VISIT LOGS"}</h3>
              <span className="filter-count">Showing <strong>{filteredLogs.length}</strong> entries</span>
            </div>
            <div className="table-filters">
              <div className="filter-group"><LuGlobe size={14} /><select value={filterCountry} onChange={(e) => { setFilterCountry(e.target.value); setCurrentPage(1); }}>{filterOptions.countries.map(c => <option key={c} value={c}>{c === "ALL" ? "All Countries" : c}</option>)}</select></div>
              <div className="filter-group"><LuFilter size={14} /><select value={filterEnv} onChange={(e) => { setFilterEnv(e.target.value); setCurrentPage(1); }}>{filterOptions.envs.map(e => <option key={e} value={e}>{e === "ALL" ? "All Environments" : e}</option>)}</select></div>
              <div className="pagination-controls">
                <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}><LuChevronLeft /></button>
                <span>{currentPage} / {totalPages || 1}</span>
                <button disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage((p) => p + 1)}><LuChevronRight /></button>
              </div>
            </div>
          </div>
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead><tr>
                <th onClick={() => requestSort(viewMode === "REALTIME" ? "last_activity" : "visited_at")} className="pointer">TIME <LuArrowUpDown size={12} /></th>
                <th onClick={() => requestSort("ip_address")} className="pointer">IP <LuArrowUpDown size={12} /></th>
                <th>COUNTRY</th><th>ENVIRONMENT</th><th>SOURCE</th><th>USER AGENT</th>
              </tr></thead>
              <tbody>{pagedItems.map((log, i) => (
                <tr key={i}>
                  <td className="time-col">{viewMode === "REALTIME" ? log.last_activity?.substring(11, 19) : log.visited_at?.substring(5, 16)}</td>
                  <td className="ip-col"><span className="ip-link" onClick={() => handleIpClick(log.ip_address)}>{log.ip_address}</span></td>
                  <td>{log.country_code}</td>
                  <td className="env-col">{getEnvironmentLabel(log.user_agent)}</td>
                  <td className="referer-col">{getSourceLabel(log.referer, log.user_agent)}</td>
                  <td className="ua-col" title={log.user_agent}>{log.user_agent}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </section>
      </div>

      {selectedIp && (
        <div className="admin-modal-overlay" onClick={() => setSelectedIp(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head"><h3>User Trace: {selectedIp}</h3><button onClick={() => setSelectedIp(null)}>×</button></div>
            <div className="modal-body">
              <div className="trace-list">
                {traceData.map((t, i) => (
                  <div key={i} className="trace-item">
                    <div className="trace-top-row">
                      <span className="trace-time">{t.visited_at}</span>
                      <span className="trace-source">{getEnvironmentLabel(t.user_agent)} | {getSourceLabel(t.referer, t.user_agent)}</span>
                      <br/>
                    </div>
                    <div className="trace-referer-detail">
                      <div className="original-url" title={t.referer || "Direct"}>
                        {t.referer && t.referer !== "Direct" ? t.referer : "No Referral (Direct Access)"}
                      </div>
                    </div>
                    <div className="trace-ua-detail">
                      <br/>
                      {t.user_agent}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}